import apiService from "@/lib/api";

const USERS_BASE = "/api/v2/users";

/**
 * Dummy service implementation (no backend yet).
 *
 * DataTableNew expects:
 *   res.data.users (array) and res.data.pagination
 */
export const UserService = {
    /**
     * `crossTenant: true` asks for the platform-wide list instead of the
     * tenant a SuperAdmin is currently working inside. It is a request
     * setting, not a filter, so it never reaches the query string.
     */
    fetchUsersList: async ({
        page = 1,
        per_page = 10,
        pagination = true,
        search = '',
        crossTenant = false,
        ...param
    }) => {
        const params = {
            page,
            per_page,
            pagination,
            ...(search ? { search } : {}),
            ...param
        };
        try {
            const response = await apiService.getSecure(`${USERS_BASE}`, { params, crossTenant });
            return response.data;
        } catch (error) {
            console.error('Error while fetching list:', error);
            throw error;
        }
    },

    /**
     * The pool a manager may be picked from, for the role being assigned.
     *
     * The server's rule is ancestry — any user whose role sits ABOVE this one
     * in the tree, however far above — and `GET /users/eligible-managers`
     * answers exactly that question. The Add/Edit form used to ask a narrower
     * one (`GET /users?roleId=<the role's immediate parent>`), so on a
     * three-level tree with the middle role unfilled the dropdown came back
     * empty and the admin who could have managed the user was never offered.
     */
    fetchEligibleManagers: async ({
        roleId,
        page = 1,
        per_page = 50,
        pagination = true,
        search = '',
        ...param
    }) => {
        const params = {
            roleId,
            page,
            per_page,
            pagination,
            ...(search ? { search } : {}),
            ...param,
        };
        try {
            const response = await apiService.getSecure(`${USERS_BASE}/eligible-managers`, { params });
            return response.data;
        } catch (error) {
            console.error('Error while fetching eligible managers:', error);
            throw error;
        }
    },

    createUser: async (payload) => {
        try {
            const response = await apiService.postSecure(`${USERS_BASE}/invite`, payload);
            return response.data;
        } catch (error) {
            console.error('Error while creating user:', error);
            throw error;
        }
    },

    updateUser: async (id, payload) => {
        try {
            const response = await apiService.patch(`${USERS_BASE}/${id}`, payload);
            return response.data;
        } catch (error) {
            console.error('Error while creating user:', error);
            throw error;
        }
    },

    deleteUser: async (id, reason) => {
        try {
            const response = await apiService.delete(`${USERS_BASE}/${id}`, {
                data: { reason },
            });
            return response.data;
        } catch (error) {
            console.error('Error while deleting user:', error);
            throw error;
        }
    },

    resendInvite: async (id) => {
        try {
            const response = await apiService.postSecure(`${USERS_BASE}/resend-invite/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error while changing the password:', error);
            throw error;
        }
    },

    /**
     * Dev/UAT-only — fetch a freshly minted invite link for a still-pending
     * user so QA can test the accept-invite flow with dummy email addresses.
     * Returns `null` when the endpoint is disabled in production (404) or
     * when the user has already accepted (backend returns empty link).
     */
    fetchDevInviteLink: async (userId) => {
        try {
            const response = await apiService.getSecure(`${USERS_BASE}/${userId}/dev-invite-link`);
            return response?.data?.inviteLink || null;
        } catch (error) {
            // 404 = flag off in this environment. Silently no-op.
            if (error?.response?.status === 404) {
                return null;
            }
            console.error('Error while fetching dev invite link:', error);
            throw error;
        }
    },

    // `updateUserDetails` lived here: no caller, and a body of `{name}` alone
    // against a route whose `roleId` is required — a 422 waiting for whoever
    // wired it up. `updateUser` is the one that works.
};

